import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Phone, Lock, AlertCircle, Stethoscope } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/hooks/useAuth';
import { authApi } from '@/api/auth';
import toast from 'react-hot-toast';

const loginSchema = z.object({
  identifier: z.string().min(1, 'Email or phone is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

const otpSchema = z.object({
  otp: z.string().length(6, 'OTP must be 6 digits'),
});

type LoginForm = z.infer<typeof loginSchema>;
type OTPForm = z.infer<typeof otpSchema>;

export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<'login' | 'otp'>('login');
  const [identifier, setIdentifier] = useState('');
  const { login, loginPending, verifyOTP, otpPending } = useAuth();

  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { identifier: '', password: '' },
  });

  const otpForm = useForm<OTPForm>({
    resolver: zodResolver(otpSchema),
    defaultValues: { otp: '' },
  });

  const onLogin = (data: LoginForm) => {
    setIdentifier(data.identifier);
    login(
      { identifier: data.identifier, password: data.password },
      {
        onSuccess: (result) => {
          if (result.requiresOTP) {
            setStep('otp');
            // Auto-send OTP
            authApi.requestOTP(data.identifier).catch(() => {});
          }
        },
      },
    );
  };

  const onOTPSubmit = (data: OTPForm) => {
    verifyOTP({ identifier, otp: data.otp });
  };

  const resendOTP = async () => {
    try {
      await authApi.requestOTP(identifier);
      toast.success('OTP resent successfully');
    } catch {
      toast.error('Failed to resend OTP');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-navy via-brand-navy-light to-brand-cyan/20 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '40px 40px',
      }} />

      <div className="relative w-full max-w-md">
        {/* Logo Card */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-4 text-center border border-white/20">
          <img
            src="/logos/logo-dark.png"
            alt="HeptaCare Health"
            className="h-10 mx-auto mb-3"
            onError={e => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <div className="flex items-center justify-center gap-2 text-white/80 text-sm">
            <Stethoscope size={16} />
            <span>Medical CRM Platform</span>
          </div>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {step === 'login' ? (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-900">Welcome back</h1>
                <p className="text-slate-500 text-sm mt-1">Sign in to your account to continue</p>
              </div>

              <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
                <Input
                  {...loginForm.register('identifier')}
                  label="Email / Phone"
                  placeholder="Enter email or mobile number"
                  error={loginForm.formState.errors.identifier?.message}
                  leftIcon={<Phone size={16} />}
                  autoComplete="username"
                />

                <Input
                  {...loginForm.register('password')}
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  error={loginForm.formState.errors.password?.message}
                  leftIcon={<Lock size={16} />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  }
                  autoComplete="current-password"
                />

                <Button type="submit" className="w-full" loading={loginPending} size="lg">
                  Sign In
                </Button>
              </form>

              <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                <div className="flex items-start gap-2 text-slate-500 text-xs">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  <p>
                    This system contains Protected Health Information (PHI).
                    All access is logged for HIPAA compliance. Unauthorized access is prohibited.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-900">Verify OTP</h1>
                <p className="text-slate-500 text-sm mt-1">
                  Enter the 6-digit code sent to <span className="font-medium text-slate-700">{identifier}</span>
                </p>
              </div>

              <form onSubmit={otpForm.handleSubmit(onOTPSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Verification Code</label>
                  <input
                    {...otpForm.register('otp')}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    className="w-full h-14 text-center text-3xl font-bold tracking-widest rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan/30 focus:border-brand-cyan"
                    autoComplete="one-time-code"
                  />
                  {otpForm.formState.errors.otp && (
                    <p className="mt-1 text-xs text-red-500">{otpForm.formState.errors.otp.message}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" loading={otpPending} size="lg">
                  Verify & Login
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setStep('login')}
                    className="text-sm text-slate-500 hover:text-brand-cyan mr-4"
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    onClick={resendOTP}
                    className="text-sm text-brand-cyan hover:text-brand-cyan-dark font-medium"
                  >
                    Resend OTP
                  </button>
                </div>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-white/40 text-xs mt-4">
          © {new Date().getFullYear()} HeptaCare Health. HIPAA & GDPR Compliant.
        </p>
      </div>
    </div>
  );
}
